import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface Props {
  icon?: ReactNode;
  label: string;
  value: string | number;
  change?: { value: string; trend: 'up' | 'down' | 'neutral' };
  hint?: string;
  className?: string;
  to?: string;
}

export function StatCard({ icon, label, value, change, hint, className, to }: Props) {
  const body = (
    <>
      <div className="mb-1.5 flex items-start justify-between gap-2 sm:mb-3 sm:items-center">
        <div className="min-w-0 text-eyebrow font-semibold uppercase text-muted-foreground">
          {label}
        </div>
        {icon && (
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-50/80 text-brand-600 dark:text-brand-400 sm:h-8 sm:w-8">
            {icon}
          </div>
        )}
      </div>
      <div className="tabular flex items-baseline gap-2">
        <div className="text-xl font-semibold leading-none tracking-tight text-foreground sm:text-2xl">
          {value}
        </div>
        {change && change.value && (
          <span
            className={cn(
              'text-tiny font-semibold',
              change.trend === 'up'
                ? 'text-emerald-600 dark:text-emerald-400'
                : change.trend === 'down'
                ? 'text-red-500'
                : 'text-muted-foreground',
            )}
          >
            {change.trend === 'up' ? '\u2191' : change.trend === 'down' ? '\u2193' : '\u2192'} {change.value}
          </span>
        )}
      </div>
      {hint && <div className="mt-1.5 text-tiny text-muted-foreground">{hint}</div>}
    </>
  );

  const baseClass = cn(
    'block rounded-xl border border-border/80 bg-card p-3 shadow-card transition-all duration-200 sm:p-5',
    to && 'cursor-pointer hover:border-brand-200 hover:shadow-pop hover:-translate-y-0.5',
    !to && 'hover:border-border',
    className,
  );

  if (to) {
    return <Link to={to} className={baseClass}>{body}</Link>;
  }
  return <div className={baseClass}>{body}</div>;
}
